/**
 * The things this feature needs to do to the address bar, and nothing more.
 *
 * THE ROUTER HAS LANDED, AND THIS IS STILL NOT ONE. adr/0007-web-app-stack.md rules TanStack
 * Router and `src/router.tsx` now mounts it. The screens in this feature keep taking the
 * four-function `Navigation` surface below rather than calling the router themselves, which keeps
 * each of them renderable in a test with a fake one. `routerNavigation` is the adapter the router
 * hands them; `browserNavigation` stays as the default for a screen rendered with no router at all.
 *
 * ONE OF THESE IS A SECURITY MEASURE AND NOT A CONVENIENCE. `stripQueryParameter` exists because
 * the hand-off code arrives in the URL — `authors.moonx.app/enter?code=…` — and a code is a
 * CREDENTIAL. Left alone it sits in the address bar, in the browser's history, in the referrer of
 * anything the page loads next, and in any screen-share. It is removed with `replace`, which also
 * means Back cannot re-land on a spent code.
 *
 * SO IS ANOTHER: `safeReturnPath`. The session gate sends a signed-out visitor to
 * `/sign-in?next=<where they were>`, and a `next` read back off a URL is attacker-controlled. Only
 * a same-origin path is ever followed, so a crafted sign-in link cannot bounce a freshly signed-in
 * author to somebody else's site.
 */
import type { RouterHistory } from '@tanstack/react-router';

/** Where the cold-start door lives. `/enter` is the one route the corpus fixes; this one is ours. */
export const SIGN_IN_PATH = '/sign-in';
/** FIXED BY THE CORPUS: "the app opens authors.moonx.app/enter?code=…" (adr/0007). */
export const ENTER_PATH = '/enter';
/**
 * The cabinet's own home - where a sign-in or a hand-off lands when it was not sent from anywhere.
 * Ours to name (decision-log row 99).
 *
 * ★ IT IS `/dashboard`, NOT `/`, AND IT WAS `/` UNTIL THE ROUTER MOUNTED THIS FEATURE. `/` renders
 * M0's shell, and it has to: two gate assertions outside this app read that exact address
 * (`src/router.tsx` header, "`/` STILL RENDERS THE M0 SHELL"), and the shell says of itself "It is
 * not the cabinet" (`src/App.tsx`). The cabinet's first screen is Overview, which
 * `features/cabinet/routes.ts` mounts at `/dashboard`. Landing a freshly signed-in author on
 * a settings table would be a dead end one click from where they meant to be. The router spec
 * asserts this path is mounted, so the two cannot drift apart silently.
 */
export const CABINET_HOME_PATH = '/dashboard';

/** The query parameter the session gate writes and the sign-in route reads. Ours to name. */
export const RETURN_TO_PARAM = 'next';

/**
 * Where the terms interstitial lives. Ours to name, like `/sign-in`: `routes/support/routes.tsx`
 * mounts the route AT this constant, so the gate that sends people here and the route that answers
 * cannot name two different addresses.
 */
export const TERMS_PATH = '/terms';

export interface Navigation {
  readonly currentSearch: () => string;
  readonly currentPath: () => string;
  /** Adds a history entry. */
  readonly push: (path: string) => void;
  /** Replaces the current entry — Back skips it. */
  readonly replace: (path: string) => void;
}

export const browserNavigation: Navigation = {
  currentSearch: () => window.location.search,
  currentPath: () => window.location.pathname,
  push: (path: string) => {
    window.history.pushState(null, '', path);
    // The History API does not notify anybody by itself. One synthetic event keeps whatever is
    // listening in step.
    window.dispatchEvent(new PopStateEvent('popstate'));
  },
  replace: (path: string) => {
    window.history.replaceState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  },
};

/**
 * The same four functions over the router's own history, which is what the mounted routes use.
 *
 * WHY NOT `browserNavigation` UNDER THE ROUTER. It writes `null` into `history.state`, and the
 * router keys its entries on the index it keeps there - `@tanstack/history` compares
 * `state.__TSR_index` on every popstate to tell Back from Forward. Going through
 * `history.push` / `history.replace` keeps that bookkeeping intact and notifies the router once,
 * and it is the only form that works under the memory history a test mounts.
 */
export function routerNavigation(history: RouterHistory): Navigation {
  return {
    currentSearch: () => history.location.search,
    currentPath: () => history.location.pathname,
    push: (path: string) => {
      history.push(path);
    },
    replace: (path: string) => {
      history.replace(path);
    },
  };
}

/**
 * Removes one query parameter from a URL and returns the path to replace with.
 *
 * Pure and exported so it can be tested without a browser: this is the function that decides
 * whether a live credential stays in the address bar.
 */
export function stripQueryParameter(path: string, search: string, name: string): string {
  const params = new URLSearchParams(search);
  params.delete(name);
  const rest = params.toString();
  return rest.length > 0 ? `${path}?${rest}` : path;
}

/**
 * A return path that is safe to follow, or `null`.
 *
 * SAME-ORIGIN PATHS ONLY. It must start with ONE `/`: `//evil.example` is a protocol-relative URL
 * and `/\evil.example` is read as one by browsers, so both are refused, as is anything with a
 * scheme. The two unauthenticated routes are refused too - returning to `/sign-in` after signing
 * in is a loop, and returning to `/enter` would re-open a spent hand-off.
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2048) {
    return null;
  }
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return null;
  }
  // A control character has no business in a path, and a tab or a newline is how a naive check
  // gets split. `\p{Cc}` rather than a `\u` range, which `no-control-regex` refuses.
  if (/\p{Cc}/u.test(raw)) {
    return null;
  }
  const pathname = raw.split(/[?#]/, 1)[0] ?? raw;
  if (pathname === SIGN_IN_PATH || pathname === ENTER_PATH) {
    return null;
  }
  return raw;
}

/** `/sign-in`, carrying where to go back to when that is worth carrying. */
export function signInPathFor(returnTo: string | null): string {
  const safe = safeReturnPath(returnTo);
  if (safe === null || safe === CABINET_HOME_PATH) {
    return SIGN_IN_PATH;
  }
  const params = new URLSearchParams({ [RETURN_TO_PARAM]: safe });
  return `${SIGN_IN_PATH}?${params.toString()}`;
}

/** Where a completed sign-in or hand-off goes: the carried return path, else the cabinet's home. */
export function landingPathFrom(search: string): string {
  return returnPathFrom(search) ?? CABINET_HOME_PATH;
}

/** The carried return path alone, or `null` when there is none worth following. */
export function returnPathFrom(search: string): string | null {
  return safeReturnPath(new URLSearchParams(search).get(RETURN_TO_PARAM));
}

/**
 * `/terms`, carrying where to go back to. Unlike `signInPathFor` the cabinet's home IS carried: the
 * interstitial's way on is exactly the screen the person was opening, and without a `next` it falls
 * back to the support page rather than to the home.
 */
export function termsPathFor(returnTo: string | null): string {
  const safe = safeReturnPath(returnTo);
  if (safe === null) {
    return TERMS_PATH;
  }
  const params = new URLSearchParams({ [RETURN_TO_PARAM]: safe });
  return `${TERMS_PATH}?${params.toString()}`;
}
